import React, { useState } from "react";
import axios from "axios";
import { FormData } from "../../../types";
import "../../../css/ViewFormModal.css";
import ConfirmationModal from "./ConfirmationModal";
import api from '../../../api/axiosConfig';
import { showToast } from "../../Shared/Toaster";

interface ViewFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  formData: FormData;
  onEdit: () => void;
  onDisable: () => void;
}

const ViewFormModal: React.FC<ViewFormModalProps> = ({
  isOpen,
  onClose,
  formData,
  onEdit,
  onDisable,
}) => {
  // const { instance, accounts } = useMsal();
  const [isEditing, setIsEditing] = useState(false);
  const [updatedForm, setUpdatedForm] = useState<FormData>(formData);
  const [loading, setLoading] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  if (!isOpen) return null;


  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setUpdatedForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
  setLoading(true);
  try {
    const token = sessionStorage.getItem("accessToken");

    const payload = {
      ...updatedForm,
      key: updatedForm.name.toUpperCase(), // ✅ Add key same as AddForm
    };

    await api.auth_api.patch(`/forms/${formData._id}`, payload, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    showToast("Form updated successfully ✅", "success");
    setIsEditing(false);
    onEdit();   // refresh parent table
    onClose();  // close modal
  } catch (err: any) {
    console.error("Error updating form:", err);
    showToast(err.response?.data?.message || "Error updating form ❌", "error");
  } finally {
    setLoading(false);
  }
};



  return (
    <div className="form-modal-overlay" onClick={onClose}>
      <div
        className="form-modal-container"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="form-modal-header">
          <h2>View New Form</h2>
          <button className="form-close-btn" onClick={onClose}>
            ✖
          </button>
        </div>

        {/* Body */}
        <div className="form-modal-body">
          {/* Name */}
          <div className="form-form-detail">
            <p className="form-label">Form Name</p>
            {isEditing ? (
              <input
                type="text"
                name="name"
                value={updatedForm.name}
                onChange={handleChange}
                className="form-input-field"
              />
            ) : (
              <p className="form-value bold">{formData.name}</p>
            )}
          </div>

          {/* Category */}
          <div className="form-form-detail">
            <p className="form-label">Category</p>
            {isEditing ? (
              <input
                type="text"
                name="category"
                value={updatedForm.category}
                onChange={handleChange}
                className="form-input-field"
              />
            ) : (
              <p className="form-value">{formData.category}</p>
            )}
          </div>

          {/* Created On */}
          <div className="form-form-detail">
            <p className="form-label">Created On</p>
            <p className="form-value">{formData.createdAt}</p>
          </div>

          {/* Disabled On */}
          <div className="form-form-detail">
            <p className="form-label">Disabled On</p>
            <p className="form-value">{formData.disabledOn || "-"}</p>
          </div>

          {/* Version */}
          <div className="form-form-detail">
            <p className="form-label">Version Number</p>
            {isEditing ? (
              <input
                type="text"
                name="version"
                value={updatedForm.version}
                onChange={handleChange}
                className="form-input-field"
              />
            ) : (
              <p className="form-value">{formData.version}</p>
            )}
          </div>

          {/* Details */}
          <div className="form-form-detail">
            <p className="form-label">Details</p>
            {isEditing ? (
              <textarea
                name="definition"
                value={updatedForm.definition}
                onChange={handleChange}
                className="form-textarea-field"
              />
            ) : (
              <p className="form-value">{formData.definition}</p>
            )}
          </div>
        </div>


        {/* Footer */}
        <div className="form-modal-footer">
          {isEditing ? (
            <button
              className="form-btn btn-primary"
              onClick={handleSave}
              disabled={loading}
            >
              {loading ? "Saving..." : "Save"}
            </button>
          ) : (
            <button
              className="form-btn btn-primary"
              onClick={() => setIsEditing(true)}
            >
              Edit
            </button>
          )}

          <button
            className="form-btn btn-secondary"
            onClick={() => setIsModalOpen(true)}
          >
            {updatedForm.disabledOn ? "Enable" : "Disable"}
          </button>
        </div>

        {/* Confirmation Modal */}
        {isModalOpen && (
          <ConfirmationModal
            message={`Are you sure you want to ${updatedForm.disabledOn ? "enable" : "disable"
              } this?`}
            onConfirm={async () => {
              try {

                const token = sessionStorage.getItem("accessToken");

                const res = await api.auth_api.patch(`/forms/${formData._id}/toggle`,
                  {},
                  {
                    headers: {
                      Authorization: `Bearer ${token}`,
                      "Content-Type": "application/json",
                    },
                  }
                );

                // ✅ Update local state immediately
                setUpdatedForm((prev) => ({
                  ...prev,
                  disabledOn: prev.disabledOn ? null : new Date().toISOString(),
                }));

                showToast(`Form ${updatedForm.disabledOn ? "enabled" : "disabled"} successfully ✅`, "success");
                onEdit(); // refresh parent table
                setIsModalOpen(false); // close confirmation modal
                onClose(); // close view modal
              } catch (err: any) {
                console.error("Error toggling form status:", err);
                showToast(err.response?.data?.message || "Error toggling form status ❌", "error");
              }
            }}
            onCancel={() => setIsModalOpen(false)}
          />
        )}

      </div>
    </div>
  );
};

export default ViewFormModal;
